import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './MagneticWallSheet.module.scss';
import Button from '../components/Button';
import { useQuill } from '../editor/QuillContext';
import closeIcon from '../icons/non-prefilled/16x16/clear.svg';

interface MagneticWallSheetProps {
  title: string;
  onClose: () => void;
  headerContent?: React.ReactNode;
  overflowMenu?: React.ReactNode;
  inputRow?: React.ReactNode;
  children?: React.ReactNode;
}

const MagneticWallSheet = ({
  title,
  onClose,
  headerContent,
  overflowMenu,
  inputRow,
  children,
}: MagneticWallSheetProps) => {
  const { quill } = useQuill();
  const portal = document.getElementById('root');

  const headerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef<number | undefined>(undefined);

  if (scrollTopRef.current === undefined) {
    scrollTopRef.current = window.pageYOffset;
    window.scrollTo(0, 0);

    // Apply layout changes before input gets focus to avoid
    // page jump issues.
    document.documentElement.classList.add(styles.overlay);
  }

  useEffect(() => {
    const updateElementShadow = () => {
      if (window.pageYOffset > 0) {
        headerRef.current?.classList.add(styles.isSticky);
      } else {
        headerRef.current?.classList.remove(styles.isSticky);
      }

      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      const contentHeight = document.documentElement.scrollHeight;
      if (window.pageYOffset + viewportHeight < contentHeight) {
        inputRef.current?.classList.add(styles.isSticky);
      } else {
        inputRef.current?.classList.remove(styles.isSticky);
      }
    };
    window.addEventListener('scroll', updateElementShadow);
    return () => window.removeEventListener('scroll', updateElementShadow);
  }, []);

  useEffect(() => {
    if (!document.documentElement.classList.contains(styles.overlay)) {
      // Only necessary when quill instance changed
      document.documentElement.classList.add(styles.overlay);
    }
    return () => {
      document.documentElement.classList.remove(styles.overlay);
      if (scrollTopRef.current !== undefined) {
        window.scrollTo(0, scrollTopRef.current);
      }
    };
  }, [quill]);

  if (!portal) return null;

  return createPortal(
    <div className={styles.container}>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.titleRow}>
          <h4>{title}</h4>
          {!!overflowMenu && (
            <div className={styles.overflowMenu}>{overflowMenu}</div>
          )}
          <Button icon={closeIcon} onClick={onClose} aria-label="Close" />
        </div>
        {headerContent}
      </div>
      {children}
      {!!inputRow && (
        <div className={styles.inputRow} ref={inputRef}>
          {inputRow}
        </div>
      )}
    </div>,
    portal,
  );
};

export default MagneticWallSheet;
